import React, { FC } from 'react';
import { style } from 'typestyle';
import { Column, Row } from '@src/components/Utils';
import { MediaView, IGroup } from 'common';
import { BulkMediaActions } from '@src/components/BulkMediaActions';
import { Hide } from '@src/components/Hide';
import { MediaActionButtonController } from '@src/fragments/MediaActionButton/MediaActionController';
import { Browsing } from '@src/types/Browsing';
import { IPopupsService } from '@src/types/Popups';
import { WithMediaSelection } from '@src/types/MediaSelection';
import { ActionButton } from '@src/components/ActionButton';
import { isMySelectionSectionFilled } from '@src/utils/QS';
import { useAtomValue } from 'jotai';
import { getFilter } from '../Selectors/Selectors';
import { HideStoriesControl, SearchModeControls } from '@src/fragments/MediaLibraryControls';

interface Props extends WithMediaSelection {
  isExactMode: boolean;
  estimatedCount: number;
  exactCount: number;
  libraryFetching: boolean;
  services: {
    popups: IPopupsService;
    browsing: Browsing;
  };
  collectAllSelected: (medias: MediaView[]) => void;
  onSearchModeChange: (exact: boolean) => void;
  onSelectAll: () => void;
  selectAllActive: boolean;
  setSelectAllActive: (active: boolean) => void;
  isFetchingAll: boolean;
  catalogCountries?: string[];
  isFetchingStories: boolean;
  setIsFetchingStories: (val: boolean) => void;
  featureFlags?: IGroup["feature_flags"];
}

export const LibraryHeader: FC<Props> = (props) => {
  const crmEnabled = props.featureFlags?.crm || false;

  // Get filter state from jotai
  const filterQsState = useAtomValue(getFilter);
  const isQsEmptyValues = !isMySelectionSectionFilled(filterQsState);

  return (
    <Row className={libraryHeaderClass}>
      <Column className={selectOptionsClass}>
        <div className={actionGroupClass}>
          {crmEnabled ? (
            <HideStoriesControl
              isFetchingStories={props.isFetchingStories}
              setIsFetchingStories={props.setIsFetchingStories}
            />
          ) : null}

          {/* "Select All" Button */}
          <button
            className={buttonClass}
            onClick={props.onSelectAll}
            disabled={props.selectAllActive || props.isFetchingAll}
            title="Up to 10 000 media will be selected"
          >
            {props.isFetchingAll ? 'Selecting...' : 'Select All'}
          </button>

          {/* BulkMediaActions */}
          <BulkMediaActions
            className={bulkMediaActionsClass}
            count={props.mediaSelection.length}
            onClear={() => {
              props.setMediaSelection([]);
              props.setSelectAllActive(false);
            }}
            mediaActionButton={(
              <MediaActionButtonController
                inCollection={undefined}
                mediaList={props.mediaSelection}
                services={props.services}
                catalogCountries={props.catalogCountries}
              />
            )}
          />

          {/* Button to create auto collection */}
          <Hide hide={isQsEmptyValues}>
            <ActionButton
              className={buttonAutoCollection}
              onClick={() => {
                props.services.popups.openCollectOverlay("new", [], true)
              }}
            >
              Create Auto Collection
            </ActionButton>
          </Hide>
        </div>
      </Column>

      <SearchModeControls
        isExactMode={props.isExactMode}
        estimatedCount={props.estimatedCount}
        exactCount={props.exactCount}
        isFetching={props.libraryFetching}
        onSearchModeChange={props.onSearchModeChange}
      />
    </Row>
  );
}

const libraryHeaderClass = style({
  fontSize: 14,
  justifyContent: 'space-between',
});

const bulkMediaActionsClass = style({
  alignItems: 'center',
});

const buttonClass = style({
  fontSize: 13,
  textAlign: 'right',
  background: 'none',
  border: 'none',
  outline: 'none',
  color: 'gray',
  fontWeight: 'bold',
  cursor: 'pointer',
  padding: 0,
  $nest: {
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      cursor: 'default',
      textDecoration: 'none',
    },
  },
});

const buttonAutoCollection = style({
  fontSize: "9px !important",
  borderRadius: 100,
  fontWeight: 500,
  padding: "2px 5px !important",
  minWidth: "unset !important",
});

const selectOptionsClass = style({
  justifyContent: 'flex-end',
  flexGrow: 0,
});

const actionGroupClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: '16px', // Add space between "Select All" and BulkMediaActions
});
